import { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import './Toast.css';

const ToastContext = createContext(null);

const ICONS = {
  success: '✓',
  danger: '✕',
  warning: '!',
  info: 'i',
};

function ToastViewport({ toasts, dismiss }) {
  const reduceMotion = useReducedMotion();

  return createPortal(
    <div className="ds-toast-viewport" aria-live="polite" aria-atomic="false">
      <AnimatePresence>
        {toasts.map((t) => (
          <motion.div
            key={t.id}
            role={t.variant === 'danger' ? 'alert' : 'status'}
            className={`ds-toast ds-toast-${t.variant}`}
            layout={!reduceMotion}
            initial={reduceMotion ? { opacity: 0 } : { opacity: 0, y: -12, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={reduceMotion ? { opacity: 0 } : { opacity: 0, x: 40, transition: { duration: 0.15 } }}
            transition={{ duration: 0.2, ease: [0.4, 0, 0.2, 1] }}
          >
            <span className="ds-toast-icon">{ICONS[t.variant] || ICONS.info}</span>
            <span className="ds-toast-message">{t.message}</span>
            <button className="ds-toast-close" onClick={() => dismiss(t.id)} aria-label="Dismiss notification">
              ✕
            </button>
          </motion.div>
        ))}
      </AnimatePresence>
    </div>,
    document.body
  );
}

export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([]);
  const idRef = useRef(0);

  const dismiss = useCallback((id) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const show = useCallback(
    (message, { variant = 'info', duration = 4000 } = {}) => {
      const id = ++idRef.current;
      setToasts((prev) => [...prev, { id, message, variant }]);
      if (duration > 0) {
        setTimeout(() => dismiss(id), duration);
      }
      return id;
    },
    [dismiss]
  );

  // ⚠️ MEMOISED, AND THAT IS LOAD-BEARING — this object is the context value,
  // so its identity is `toast` in every consumer.
  //
  // As a bare object literal it was rebuilt on every provider render, and the
  // provider re-renders on every `setToasts` — showing a toast, and again when
  // the 4s timer dismisses it. So `toast` changed identity each time ANY toast
  // anywhere appeared or expired, and any consumer listing it in a hook's
  // dependency array re-ran.
  //
  // Where that hook was a fetch whose failure shows a toast, it closed a loop:
  // request fails → toast.danger → new identity → effect re-runs → request
  // fails → … a tight retry at network speed, for as long as the error lasted.
  // (Documents was hammering /api/documents/summary this way.)
  //
  // `show` and `dismiss` are already stable useCallbacks, so this memo never
  // recomputes and `toast` is stable for the life of the provider. Consumers
  // may safely put it in a dependency array — several already assume they can.
  const api = useMemo(
    () => ({
      show,
      success: (message, opts) => show(message, { ...opts, variant: 'success' }),
      danger: (message, opts) => show(message, { ...opts, variant: 'danger' }),
      warning: (message, opts) => show(message, { ...opts, variant: 'warning' }),
      info: (message, opts) => show(message, { ...opts, variant: 'info' }),
      dismiss,
    }),
    [show, dismiss]
  );

  return (
    <ToastContext.Provider value={api}>
      {children}
      <ToastViewport toasts={toasts} dismiss={dismiss} />
    </ToastContext.Provider>
  );
}

export function useToast() {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used within a ToastProvider');
  return ctx;
}
